import { useEffect, useRef } from 'react';
import { useReferenceStore } from '@/store/reference.store.js';
import { useTenantStore } from '@/store/tenant.store.js';
import { logReferenceDetailSync } from '@/lib/reference-sync/reference-detail-sync-debug.js';

const pendingSkip = new Map();

/**
 * @param {string} entityKey
 */
export function skipNextRefetchForEntity(entityKey) {
    if (!entityKey) return;
    pendingSkip.set(entityKey, true);
}

/**
 * @param {string[]} entityKeys
 */
export function skipNextRefetchForEntities(entityKeys) {
    if (!entityKeys?.length) return;
    for (const key of entityKeys) {
        skipNextRefetchForEntity(key);
    }
}

function consumeSkip(entityKey) {
    if (!pendingSkip.get(entityKey)) return false;
    pendingSkip.delete(entityKey);
    return true;
}

/**
 * @param {string[]} entityKeys
 * @returns {boolean}
 */
export function consumeSkipForEntities(entityKeys) {
    if (!entityKeys?.length) return false;
    return entityKeys.every((key) => consumeSkip(key));
}

/**
 * @param {string} entityKey
 * @param {() => void | Promise<void>} refetch
 * @param {{ enabled?: boolean, debounceMs?: number }} [options]
 */
export function useRefetchListOnReferenceVersion(entityKey, refetch, options = {}) {
    const { enabled = true, debounceMs = 250 } = options;
    const version = useReferenceStore((s) => (entityKey ? s.versions?.[entityKey] : undefined));
    const tenantId = useTenantStore((s) => s.selectedTenantId);

    const prevVersionRef = useRef(undefined);
    const hasInitializedRef = useRef(false);
    const prevTenantRef = useRef(tenantId);
    const debounceTimerRef = useRef(null);

    useEffect(() => {
        if (!enabled || !entityKey || !tenantId) {
            return;
        }

        if (prevTenantRef.current !== tenantId) {
            prevTenantRef.current = tenantId;
            prevVersionRef.current = undefined;
            hasInitializedRef.current = false;
        }

        const current = version;

        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
            prevVersionRef.current = current;
            return;
        }

        if (current === prevVersionRef.current) {
            return;
        }

        prevVersionRef.current = current;

        if (consumeSkip(entityKey)) {
            logReferenceDetailSync('refetch-hook', 'SKIPPED single-entity refetch (consumeSkip)', {
                entityKey,
                version: current,
            });
            return undefined;
        }

        logReferenceDetailSync('refetch-hook', 'scheduling single-entity refetch', {
            entityKey,
            version: current,
            debounceMs,
        });

        if (debounceMs > 0) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = setTimeout(() => {
                debounceTimerRef.current = null;
                void Promise.resolve(refetch());
            }, debounceMs);
        } else {
            void Promise.resolve(refetch());
        }

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
        };
    }, [entityKey, enabled, tenantId, version, refetch, debounceMs]);
}
